// Sales → Receivables: every unpaid invoice (sales_receivables), grouped by how
// late it is, with totals, "mark paid" (finance / owner) and a CSV export.

import { useState } from "react";
import { fill, type Locale } from "../../i18n/dictionary";
import type { SalesDictionary } from "../../i18n/salesDictionary";
import { AGE_BUCKETS, toCsv, type AgeBucket, type Receivable } from "../../data/sales";
import { Button, Card, ErrorPanel, LoadingLine, StatTile, TableCard, TD, TH, formatDate, formatMoney, useLoad } from "../admin/ui";
import { SectionTitle, StatusLine, downloadCsv, salesErrorCopy, useSales } from "./shared";
import { Link } from "react-router-dom";

export function groupByBucket(list: Receivable[]): Record<AgeBucket, Receivable[]> {
  const out = Object.fromEntries(AGE_BUCKETS.map((b) => [b, [] as Receivable[]])) as Record<AgeBucket, Receivable[]>;
  for (const r of list) out[r.ageBucket].push(r);
  return out;
}

/** Totals per currency as display text. */
export function totalText(locale: Locale, list: Receivable[]): string {
  const m = new Map<string, number>();
  for (const r of list) m.set(r.currency, Math.round(((m.get(r.currency) ?? 0) + r.amount) * 100) / 100);
  if (m.size === 0) return formatMoney(locale, 0, "MYR");
  return [...m.entries()].map(([c, n]) => formatMoney(locale, n, c)).join(" + ");
}

export function receivablesCsv(t: SalesDictionary, list: Receivable[]): string {
  return toCsv([
    ["Organisation", "Invoice", "Issued", "Due", "Days late", "Age", "Amount", "Currency"],
    ...list.map((r) => [r.orgName, r.number, r.issuedAt.slice(0, 10), r.dueAt ?? "", r.daysOverdue, t.receivables[`bucket_${r.ageBucket}`], r.amount.toFixed(2), r.currency]),
  ]);
}

export function Receivables() {
  const { ds, t, can, locale } = useSales();
  const load = useLoad(() => ds.receivables(), [ds]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "good" | "attention"; text: string } | null>(null);

  async function markPaid(r: Receivable) {
    setBusy(r.invoiceId);
    setMsg(null);
    try {
      await ds.setInvoiceStatus(r.invoiceId, "paid");
      setMsg({ tone: "good", text: `${t.receivables.paid} ${r.number}` });
      await load.refresh();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setBusy(null);
    }
  }

  const list = load.data ?? [];
  const groups = groupByBucket(list);

  return (
    <section aria-labelledby="recv-title">
      <SectionTitle
        id="recv-title"
        action={
          list.length > 0 ? (
            <Button variant="secondary" onClick={() => downloadCsv(`zooylo-receivables-${new Date().toISOString().slice(0, 10)}.csv`, receivablesCsv(t, list))}>
              {t.common.exportCsv}
            </Button>
          ) : undefined
        }
      >
        {t.receivables.title}
      </SectionTitle>
      <p style={{ margin: "0 0 14px", color: "var(--ink-soft)", fontSize: 15 }}>{t.receivables.intro}</p>
      <StatusLine msg={msg} />

      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : list.length === 0 ? (
        <Card>
          <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.receivables.none}</p>
        </Card>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))", gap: 10, margin: "8px 0 18px" }}>
            <StatTile label={t.receivables.total} value={totalText(locale, list)} />
            {AGE_BUCKETS.map((b) => (
              <StatTile key={b} label={t.receivables[`bucket_${b}`]} value={totalText(locale, groups[b])} hint={fill(t.receivables.countN, { n: groups[b].length })} />
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {AGE_BUCKETS.filter((b) => groups[b].length > 0).map((b) => (
              <section key={b} aria-labelledby={`bucket-${b}`}>
                <h3 id={`bucket-${b}`} style={{ fontSize: 17, color: b === "current" ? "var(--ink-deep)" : "var(--peach-deep)", margin: "0 0 8px" }}>
                  {t.receivables[`bucket_${b}`]}
                  <span style={{ fontWeight: 600, color: "var(--ink-soft)", fontSize: 14.5 }}>
                    {" · "}
                    {fill(t.receivables.groupTotal, { n: groups[b].length, total: totalText(locale, groups[b]) })}
                  </span>
                </h3>
                <TableCard label={t.receivables[`bucket_${b}`]} minWidth={can("invoices") ? 720 : 600}>
                  <thead>
                    <tr>
                      <th scope="col" style={TH}>{t.receivables.colOrg}</th>
                      <th scope="col" style={TH}>{t.receivables.colNumber}</th>
                      <th scope="col" style={TH}>{t.receivables.colDue}</th>
                      <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.receivables.colLate}</th>
                      <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.receivables.colAmount}</th>
                      {can("invoices") && <th scope="col" style={TH}>{t.invoices.colActions}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {groups[b].map((r) => (
                      <tr key={r.invoiceId}>
                        <td style={TD}>
                          <Link to={`/sales/accounts/${encodeURIComponent(r.orgId)}`} style={{ color: "var(--brand)", fontWeight: 600 }}>
                            {r.orgName}
                          </Link>
                        </td>
                        <td style={{ ...TD, whiteSpace: "nowrap" }}>{r.number}</td>
                        <td style={{ ...TD, whiteSpace: "nowrap" }}>{formatDate(locale, r.dueAt)}</td>
                        <td style={{ ...TD, textAlign: "end" }}>{r.daysOverdue}</td>
                        <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: 700 }}>{formatMoney(locale, r.amount, r.currency)}</td>
                        {can("invoices") && (
                          <td style={TD}>
                            <Button variant="secondary" disabled={busy !== null} aria-label={`${t.receivables.markPaid}: ${r.number}`} onClick={() => void markPaid(r)}>
                              {t.receivables.markPaid}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </TableCard>
              </section>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
